'use strict';

var React = require('react');
var reactIcons = require('@sravni/react-icons');
var reactUtils = require('@sravni/react-utils');
var zIndexes = require('../../../constants/zIndexes.js');
var Icon = require('../../Icon/src/Icon.js');
var Portal = require('../../Portal/src/Portal.js');
var Space = require('../../Space/src/Space.js');
var Fade = require('../../Transition/src/Fade.js');
require('../../Transition/src/ScaleFade.js');
require('../../Transition/src/SlideFade.js');
require('../../Transition/src/Collapse.js');
require('../../Typography/src/Heading.js');
require('../../Typography/src/Link.js');
require('../../Typography/src/ListItem.js');
require('../../Typography/src/OrderedList.js');
var Text = require('../../Typography/src/Text.js');
require('../../Typography/src/UnorderedList.js');
var Tooltip_module = require('../styles/Tooltip.module.less.js');

function _interopNamespaceDefault(e) {
    var n = Object.create(null);
    if (e) {
        Object.keys(e).forEach(function (k) {
            if (k !== 'default') {
                var d = Object.getOwnPropertyDescriptor(e, k);
                Object.defineProperty(n, k, d.get ? d : {
                    enumerable: true,
                    get: function () { return e[k]; }
                });
            }
        });
    }
    n.default = e;
    return Object.freeze(n);
}

var React__namespace = /*#__PURE__*/_interopNamespaceDefault(React);

/**
 * @description The Tooltip component is used to show more content of a target
 *
 * @component
 * @example
 * ```jsx
 * <Tooltip title="prompt text">
 *   <span>Tooltip will show on mouse enter or by taps on mobile.</span>
 * </Tooltip>
 * ```
 */
const Tooltip = React__namespace.forwardRef((props, ref) => {
    const { title, content, footer, triggerMode = 'hover', closable, placement, width = 300, className, style, children, onClose, ...restProps } = props;
    const cx = reactUtils.useStyles(Tooltip_module.default);
    const popper = React__namespace.useRef(null);
    const [visible, setVisible] = reactUtils.useBoolean(false);
    const visibleDebounce = reactUtils.useDebounceValue(visible, 300);
    const { referenceRef, arrowRef, getPopperProps } = reactUtils.usePositioner({
        placement,
        offset: [0, 16],
        modifiers: [
            {
                name: 'flip',
                options: {
                    fallbackPlacements: ['top', 'right', 'bottom', 'left'],
                },
            },
        ],
    });
    const elementStyles = React__namespace.useMemo(() => {
        const styles = {
            ...style,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            '--width': `${width}px`,
        };
        styles.width = width;
        return styles;
    }, [style, width]);
    // eslint-disable-next-line @typescript-eslint/init-declarations
    let triggerElement;
    // Ensure tooltip has only one child node
    const count = React__namespace.useMemo(() => React__namespace.Children.count(children), [children]);
    const condition = count === 0 || count > 1;
    if (condition) {
        reactUtils.error({
            condition,
            message: 'Ensure tooltip has only one child node',
        })();
        triggerElement = children;
    }
    else {
        const child = React__namespace.Children.only(children);
        const handleMouseEnter = (event) => {
            setVisible.on();
            child.props.onMouseEnter?.(event);
        };
        const handleMouseLeave = (event) => {
            setVisible.off();
            child.props.onMouseLeave?.(event);
        };
        const handleClick = (event) => {
            setVisible.toggle();
            child.props.onClick?.(event);
        };
        const handleTouchEnd = (event) => {
            setVisible.toggle();
            child.props.onTouchEnd?.(event);
        };
        const triggerProps = {
            ...child.props,
            tabIndex: child.props.tabIndex ?? 0,
            className: cx('outline-none', child.props.className),
            // eslint-disable-next-line react-hooks/rules-of-hooks
            ref: reactUtils.useMergedRef(referenceRef, child.ref),
        };
        if (triggerMode === 'click') {
            triggerProps.onClick = handleClick;
        }
        else {
            triggerProps.onMouseEnter = handleMouseEnter;
            triggerProps.onMouseLeave = handleMouseLeave;
        }
        triggerProps.onTouchEnd = handleTouchEnd;
        triggerElement = React__namespace.cloneElement(child, triggerProps);
    }
    const handleClose = React__namespace.useCallback((event) => {
        setVisible.off();
        onClose?.(event);
    }, [onClose, setVisible]);
    return (React__namespace.createElement(React__namespace.Fragment, null,
        triggerElement,
        React__namespace.createElement(Portal.Portal, null,
            React__namespace.createElement(Fade.Fade, { className: cx('bubble'), visible: visibleDebounce, unmountOnExit: true, ...getPopperProps({ style: { zIndex: zIndexes.Z_INDEXES.TOOLTIP } }, popper) },
                React__namespace.createElement("div", { ref: arrowRef, className: cx('arrow') }),
                React__namespace.createElement("div", { ...restProps, "data-qa": "Tooltip", ref: ref, className: cx(className, 'container'), style: elementStyles, onMouseEnter: setVisible.on, onMouseLeave: () => triggerMode === 'hover' && setVisible.off() },
                    React__namespace.createElement(Space.Space, { direction: "vertical", size: 8 },
                        title && (React__namespace.createElement(Space.Space, { justify: "space-between", size: 8 },
                            React__namespace.createElement(Text.Text, { size: 16, strong: true }, title),
                            closable && React__namespace.createElement(Icon.Icon, { className: cx('close'), icon: React__namespace.createElement(reactIcons.Cross, null), size: 16, onClick: handleClose }))),
                        content && React__namespace.createElement(Text.Text, { size: 14 }, content),
                        footer && footer))))));
});
Tooltip.defaultProps = {
    placement: 'top',
    width: 300,
    triggerMode: 'hover',
};
Tooltip.displayName = 'Tooltip';

exports.Tooltip = Tooltip;
//# sourceMappingURL=Tooltip.js.map
